import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useStore } from './store/useStore';
import { startRealtime, stopRealtime } from './lib/realtime';
import { setUnauthorizedHandler } from './lib/apiClient';
import Layout from './components/Layout';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Timeline from './pages/Timeline';
import InvoiceTracking from './pages/InvoiceTracking';
import Chat from './pages/Chat';
import Admin from './pages/Admin';
import Finance from './pages/Finance';
import Sales from './pages/Sales';
import SalesStats from './pages/SalesStats';
import Inquiries from './pages/Inquiries';
import Tenders from './pages/Tenders';
import SalesDesign from './pages/SalesDesign';
import ErrorLog from './components/ErrorLog';

function FullScreenLoader({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="flex items-center gap-3 text-gray-400 text-sm">
        <div className="w-4 h-4 rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
        {label}
      </div>
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading, twoFactorPending } = useAuthStore();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (twoFactorPending) return <Navigate to="/login" replace />;
  if (user.mustChangePass) return <Navigate to="/change-password" replace />;
  return <>{children}</>;
}

function RequirePartialAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthStore();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * /login must never trap someone who already finished 2FA but still needs to
 * change their password — that used to render `<Login />` with empty/wrong
 * local step and could interact badly with redirects (navigation throttling).
 */
function LoginGate() {
  const { user, twoFactorPending } = useAuthStore();
  if (!user) return <Login />;
  if (twoFactorPending) return <Login />;
  if (user.mustChangePass) return <Navigate to="/change-password" replace />;
  return <Navigate to="/" replace />;
}

export default function App() {
  const { user, loading, twoFactorPending, hydrate } = useAuthStore();
  const hydrateData = useStore((s) => s.hydrate);
  const dataHydrated = useStore((s) => s.hydrated);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  // When any protected request 401s (session expired server-side), drop the
  // stale user so RequireAuth redirects to /login instead of leaving the user
  // on a page where every action fails with "Authentication required".
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (useAuthStore.getState().user) {
        stopRealtime();
        useAuthStore.setState({ user: null, twoFactorPending: false, step: 'idle' });
      }
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // Only hydrate data + open the SSE stream once the user has finished
  // 2FA. While `twoFactorPending` is true the server (correctly) returns 403
  // for every authenticated route, and we'd otherwise loop forever. We also
  // wait for `mustChangePass` to clear so the change-password screen isn't
  // racing with a workspace fetch.
  useEffect(() => {
    if (user && !twoFactorPending && !user.mustChangePass) {
      hydrateData();
      startRealtime();
      return stopRealtime;
    }
  }, [user, twoFactorPending, hydrateData]);

  if (loading) return <FullScreenLoader label="Connecting…" />;

  return (
    <BrowserRouter>
      <ErrorLog />
      <Routes>
        <Route path="/login" element={<LoginGate />} />
        <Route
          path="/change-password"
          element={
            <RequirePartialAuth>
              <ChangePassword />
            </RequirePartialAuth>
          }
        />
        <Route
          path="/"
          element={
            <RequireAuth>
              {!dataHydrated ? <FullScreenLoader label="Loading workspace…" /> : <Layout />}
            </RequireAuth>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="clients" element={<Clients />} />
          <Route path="clients/:clientId" element={<ClientDetail />} />
          <Route path="projects" element={<Projects />} />
          <Route path="projects/:projectId" element={<ProjectDetail />} />
          <Route path="projects/:projectId/timeline" element={<Timeline />} />
          <Route path="projects/:projectId/invoices" element={<InvoiceTracking />} />
          <Route path="projects/:projectId/chat" element={<Chat />} />
          <Route path="finance" element={<Finance />} />
          <Route path="sales" element={<Sales />} />
          <Route path="sales/stats" element={<SalesStats />} />
          <Route path="sales/inquiries" element={<Inquiries />} />
          <Route path="sales/tenders" element={<Tenders />} />
          <Route path="projects/design" element={<SalesDesign />} />
          <Route path="sales/design" element={<Navigate to="/projects/design" replace />} />
          <Route path="admin" element={<Admin />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
